import Form from "@/components/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import AddressFieldset from "@/components/AddressFieldset";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import Map from "@/components/Map";
import MapLink from "@/components/MapLink";
import NetworkSelect from "@/components/NetworkSelect";
import PageContent from "@/components/PageContent";
import SaveButton from "@/components/SaveButton";
import StoreFormatSelect from "@/components/StoreFormatSelect";
import StoreStatusSelect from "@/components/StoreStatusSelect";
import { CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { isRequired } from "@/lib/form";
import { writeStore } from "@/schemas/store";
import { STORE_STATUS_ACTIVE } from "@/schemas/storeStatus";
import { ApiError } from "@/services/api";
import { ReadStore, WriteStore } from "@/types/store";
import { UseMutateFunction } from "@tanstack/react-query";
import { Warehouse } from "lucide-react";
import { useForm } from "react-hook-form";

interface StoreFormProps {
  store?: Partial<ReadStore>;
  onSubmit: UseMutateFunction<ReadStore, ApiError, WriteStore>;
  disabled?: boolean;
}

const defaultStore: Partial<ReadStore> = {
  network_id: "",
  store_format_id: "",
  city: "",
  street_name: "",
  street_number: "",
  apartment_number: "",
  postal_code: "",
  province_id: "",
  store_number: "",
  store_number2: "",
  shopping_center: "",
  notes: "",
  status: STORE_STATUS_ACTIVE,
};

const StoreForm = ({
  store = defaultStore,
  onSubmit,
  disabled,
}: StoreFormProps) => {
  const { t } = useTranslation();

  const form = useForm<WriteStore>({
    resolver: zodResolver(writeStore),
    defaultValues: store,
    disabled,
  });

  useFormUnsavedChangesWarning(form);

  const handleSubmit = async (values: WriteStore) => {
    await onSubmit(values);
  };

  const handleLocationChange = (lat: number, lng: number) => {
    form.setValue("latitude", lat, {
      shouldDirty: true,
    });
    form.setValue("longitude", lng, {
      shouldDirty: true,
    });
  };

  return (
    <Form form={form} handleSubmit={handleSubmit}>
      <PageContent
        icon={<Warehouse className="w-6 h-6 mr-3" />}
        footer={<SaveButton />}
        title={t("stores.title.single")}
        subtitle={
          store.ref_number !== undefined && (
            <>
              {t("stores.fields.ref_number")}:{" "}
              <strong>{store.ref_number}</strong>
            </>
          )
        }
      >
        <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            name="network_id"
            label={t("stores.fields.network")}
            required={isRequired(writeStore.shape.network_id)}
            render={({ field }) => {
              return (
                <NetworkSelect
                  className="w-full"
                  loading="eager"
                  defaultItem={{ id: store.network_id, name: store.network }}
                  {...field}
                />
              );
            }}
          />
          <Field
            name="store_format_id"
            label={t("stores.fields.store_format")}
            required={isRequired(writeStore.shape.store_format_id)}
            render={({ field }) => (
              <StoreFormatSelect
                className="w-full"
                loading="eager"
                defaultItem={{
                  id: store.store_format_id ?? undefined,
                  name: store.store_format,
                }}
                {...field}
              />
            )}
          />
          <Field
            name="store_number"
            label={t("stores.fields.store_number")}
            required={isRequired(writeStore.shape.store_number)}
            render={({ field }) => (
              <Input className="w-full" {...field} value={field.value ?? ""} />
            )}
          />
          <Field
            name="store_number2"
            label={t("stores.fields.store_number2")}
            required={isRequired(writeStore.shape.store_number2)}
            render={({ field }) => (
              <Input className="w-full" {...field} value={field.value ?? ""} />
            )}
          />
          <Field
            name="shopping_center"
            label={t("stores.fields.shopping_center")}
            required={isRequired(writeStore.shape.shopping_center)}
            render={({ field }) => (
              <Input className="w-full" {...field} value={field.value ?? ""} />
            )}
          />
          <Field
            name="status"
            label={t("stores.fields.status")}
            required={isRequired(writeStore.shape.status)}
            render={({ field }) => (
              <StoreStatusSelect
                className="w-full"
                loading="eager"
                defaultItem={{ id: field.value, name: field.value }}
                {...field}
              />
            )}
          />
          <Field
            name="notes"
            wrapperClassName="sm:col-span-2"
            label={t("stores.fields.notes")}
            required={isRequired(writeStore.shape.notes)}
            render={({ field }) => (
              <Textarea
                className="w-full"
                {...field}
                value={field.value ?? ""}
              />
            )}
          />
        </Fieldset>
        <header className="flex flex-row justify-between space-y-0 pt-4">
          <CardTitle level={4}>{t("stores.addressSection")}</CardTitle>
          <span>
            <MapLink className="my-[-6px]" />
          </span>
        </header>
        <section className="flex flex-row flex-wrap mt-4 gap-8 @container">
          <AddressFieldset
            className="w-full @3xl:w-auto grow"
            schema={writeStore}
          >
            <AddressFieldset.Province />
            <AddressFieldset.PostalCode />
            <AddressFieldset.City />
            <AddressFieldset.StreetName />
            <AddressFieldset.StreetNumber />
            <AddressFieldset.ApartmentNumber />
            <AddressFieldset.Latitude />
            <AddressFieldset.Longitude />
          </AddressFieldset>
          <Map
            className="w-full @3xl:w-1/4 grow"
            onLocationChange={handleLocationChange}
          />
        </section>
      </PageContent>
    </Form>
  );
};

export default StoreForm;
